import { useMemo, useState } from "react"
import type { Me } from "@/api/generated/model"
import { useGetMessageStats } from "@/api/generated/messaging/messaging"
import { useListDevices } from "@/api/generated/devices/devices"
import { useAdminListTransfers } from "@/api/generated/transfers/transfers"
import { useAdminListRefunds } from "@/api/generated/refunds/refunds"
import { useAdminListPaymentsToReview } from "@/api/generated/payments/payments"
import { useAdminListJobs } from "@/api/generated/ops/ops"
import { POLL } from "@/api/query-client"
import { useVisiblePolling } from "@/api/use-visible-polling"
import { routeAllowed } from "@/auth/permissions"
import { useMe } from "@/auth/session"
import { isOffline } from "@/lib/devices"

const HOUR = 3_600_000

export interface AlertView {
  id: string
  labelKey: string
  to: string
  /** `undefined` while the first answer is on its way. */
  count: number | undefined
}

interface SourceDef {
  id: string
  permission: string
  money: boolean
  labelKey: string
  to: string
}

/** LLD §7.6, in the order the menu lists them. */
const SOURCES = [
  {
    id: "failedMessages",
    permission: "messaging.sms:read",
    money: false,
    to: "/messages?status=failed",
  },
  { id: "offlineDevices", permission: "devices.device:read", money: false, to: "/devices" },
  {
    id: "transfersToReview",
    permission: "transfers.transfer:review",
    money: true,
    to: "/admin/transfers",
  },
  {
    id: "openRefunds",
    permission: "refunds.refund:review",
    money: true,
    to: "/admin/refunds?status=open",
  },
  {
    id: "paymentsToReview",
    permission: "payments.payment:read",
    money: true,
    to: "/admin/payments/review",
  },
  {
    id: "failedJobs",
    permission: "system.job:read",
    money: false,
    to: "/admin/jobs?status=discarded",
  },
].map((s): SourceDef => ({ ...s, labelKey: `alerts.${s.id}` }))

const allowed = (me: Me | undefined) => (s: SourceDef) =>
  me !== undefined && routeAllowed({ permission: s.permission, money: s.money }, me)

/**
 * Every queue the caller may see, each asked ONLY when its permission is held
 * (a request the caller may not make would 403 on every page load), polled
 * through `useVisiblePolling` so a hidden tab costs nothing (NFR-DSH-18).
 * The admin overview's queue cards read the same hooks (LLD §9.14).
 */
export function useAlertSources(): AlertView[] {
  const { data: me } = useMe()
  const interval = useVisiblePolling(POLL.alerts)
  const on = useMemo(() => {
    const ok = allowed(me)
    const flags: Record<string, boolean> = Object.fromEntries(SOURCES.map((s) => [s.id, ok(s)]))
    return flags
  }, [me])
  // Rounded to the minute and fixed per mount: a moving `from` would be a new
  // query key on every render.
  const [since] = useState(() =>
    new Date(Math.floor((Date.now() - 24 * HOUR) / 60_000) * 60_000).toISOString(),
  )
  const q = (enabled: boolean | undefined) => ({
    query: { enabled: enabled === true, refetchInterval: interval },
  })

  const stats = useGetMessageStats({ from: since }, q(on.failedMessages))
  const devices = useListDevices({ limit: 100 }, q(on.offlineDevices))
  const transfers = useAdminListTransfers(
    { status: "submitted", limit: 100 },
    q(on.transfersToReview),
  )
  const refunds = useAdminListRefunds({ status: "open", limit: 100 }, q(on.openRefunds))
  const payments = useAdminListPaymentsToReview({ limit: 100 }, q(on.paymentsToReview))
  const jobs = useAdminListJobs({ status: "discarded", limit: 100 }, q(on.failedJobs))

  const counts: Record<string, number | undefined> = {
    failedMessages: stats.data?.buckets.reduce((n, b) => n + b.failed, 0),
    offlineDevices: devices.data?.items.filter(isOffline).length,
    transfersToReview: transfers.data?.items.length,
    openRefunds: refunds.data?.items.length,
    paymentsToReview: payments.data?.items.length,
    failedJobs: jobs.data?.items.length,
  }

  return SOURCES.filter((s) => on[s.id]).map((s) => ({
    id: s.id,
    labelKey: s.labelKey,
    to: s.to,
    count: counts[s.id],
  }))
}
